import React__default, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import Box from '../Box/Box.js';
import sx from '../../sx.js';
import Text from '../Text/Text.js';
import { themeGet } from '../../utils/theme-get.js';
import { useRefObjectAsForwardedRef } from '../../hooks/useRefObjectAsForwardedRef.js';

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
const labelAppearanceObject = {
  bold: {
    default: 'colors.text.inverse',
    inprogress: 'colors.text.inverse',
    moved: 'colors.text.warning.inverse',
    new: 'colors.text.inverse',
    removed: 'colors.text.inverse',
    success: 'colors.text.inverse',
    todo: 'colors.text.inverse',
    any: 'colors.text.inverse',
    done: 'colors.text.inverse',
    locked: 'colors.text.disabled'
  },
  default: {
    default: 'colors.text.subtle',
    inprogress: 'colors.text.information',
    moved: 'colors.text.warning.default',
    new: 'colors.text.discovery',
    removed: 'colors.text.danger',
    success: 'colors.text.success',
    todo: 'colors.text.subtle',
    any: 'colors.text.subtle',
    done: 'colors.text.success',
    locked: 'colors.text.disabled'
  }
};
const wrapperAppearanceObject = {
  bold: {
    default: 'colors.background.neutral.bold.default',
    inprogress: 'colors.background.information.bold.default',
    moved: 'colors.background.warning.bold.default',
    new: 'colors.background.discovery.bold.default',
    removed: 'colors.background.danger.bold.default',
    success: 'colors.background.success.bold.default',
    todo: 'colors.background.neutral.bold.default',
    any: 'colors.background.neutral.bold.default',
    done: 'colors.background.success.bold.default',
    locked: 'colors.background.disabled'
  },
  default: {
    default: 'colors.background.neutral.default.[default]',
    inprogress: 'colors.background.information.default.[default]',
    moved: 'colors.background.warning.default.[default]',
    new: 'colors.background.discovery.default.[default]',
    removed: 'colors.background.danger.default.[default]',
    success: 'colors.background.success.default.[default]',
    todo: 'colors.background.neutral.default.[default]',
    any: 'colors.background.neutral.default.[default]',
    done: 'colors.background.success.default.[default]',
    locked: 'colors.background.disabled'
  }
};

// @TODO: create a proper color map once we know all statuses
const Wrapper = styled(Box).withConfig({
  displayName: "AtlasLozenge__Wrapper",
  componentId: "sc-19f6zbq-0"
})(["box-sizing:border-box;display:inline-flex;border-radius:", ";padding-inline:", ";position:static;overflow:hidden;background-color:", ";", ""], themeGet('radii.2'), themeGet('space.1'), p => {
  if (p.appearance && {}.hasOwnProperty.call(wrapperAppearanceObject.bold, p.appearance) && p.isBold) return themeGet(wrapperAppearanceObject.bold[p.appearance]);
  if (p.appearance && {}.hasOwnProperty.call(wrapperAppearanceObject.bold, p.appearance)) return themeGet(wrapperAppearanceObject.default[p.appearance]);
  return 'revert';
}, sx);
const Label = styled(Text).withConfig({
  displayName: "AtlasLozenge__Label",
  componentId: "sc-19f6zbq-1"
})(["font-weight:bold;font-size:", ";line-height:", ";text-transform:uppercase;color:", ";", ""], themeGet('fontSizes.1'), themeGet('lineHeights.default'), p => {
  if (p.appearance && {}.hasOwnProperty.call(labelAppearanceObject.bold, p.appearance) && p.isBold) return themeGet(labelAppearanceObject.bold[p.appearance]);
  if (p.appearance && {}.hasOwnProperty.call(labelAppearanceObject.bold, p.appearance)) return themeGet(labelAppearanceObject.default[p.appearance]);
  return 'revert';
}, p => p.shouldTruncate && css(["overflow:hidden;text-overflow:ellipsis;white-space:nowrap;"]));

/**
 *
 * Visual indicator used to highlight an item's status for quick recognition.
 *
 * Includes additional custom statuses and can be used as a drop-in replacement in Atlassian products.
 *
 */
const AtlasLozenge = /*#__PURE__*/forwardRef(({
  children,
  testId,
  isBold = false,
  appearance = 'default',
  shouldTruncate = true,
  maxWidth = 200,
  ...props
}, forwardedRef) => {
  const innerRef = React__default.useRef(null);
  useRefObjectAsForwardedRef(forwardedRef, innerRef);
  const maxWidthValue = typeof maxWidth === 'string' ? maxWidth : `${maxWidth}px`;
  const maxWidthIsPc = typeof maxWidth === 'string' && /%$/.test(maxWidth);
  return /*#__PURE__*/React__default.createElement(Wrapper, _extends({
    as: "span",
    appearance: appearance,
    isBold: isBold,
    ref: innerRef,
    sx: {
      maxWidth: maxWidthIsPc ? maxWidth : '100%'
    },
    testId: testId
  }, props), /*#__PURE__*/React__default.createElement(Label, {
    appearance: appearance,
    shouldTruncate: shouldTruncate,
    isBold: isBold,
    sx: {
      maxWidth: maxWidthIsPc ? '100%' : `calc(${maxWidthValue} - 8px)`
    },
    "data-testid": testId && `${testId}--text`
  }, children));
});
AtlasLozenge.displayName = 'AtlasLozenge';
var AtlasLozenge$1 = AtlasLozenge;

export { AtlasLozenge$1 as default };
